import { useFormContext } from 'react-hook-form';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import FormFieldCheckbox from '@/components/FormFieldCheckbox';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import FormFieldTextArea from '@/components/FormFieldTextArea';
import FormFieldPhone from '@/components/FormFieldPhone';
import { FormFieldRadioGroup } from '@/components/FormFieldRadioGroup';
import FormFieldSearch from '@/components/FormFieldSearch';
import { FormFieldTimePicker } from '@/components/FormFieldTimePicker';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Search, Edit2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getCounties, deliveryService, User } from '@/services/deliveryService';

const PICKUP_TYPE_OPTIONS = [
  { label: 'Envelope(s)', value: 'ENVELOPE' },
  { label: 'Box(es)', value: 'BOX' },
  { label: 'Other', value: 'OTHER' },
];

const ADDITIONAL_JOB_OPTIONS = [
  { label: 'Printing', value: 'PRINTING' },
  { label: 'Other', value: 'OTHER' },
];

const searchDgrUsers = async (searchTerm: string): Promise<User[]> => {
  const result = await deliveryService.searchDgrUsers(searchTerm);
  return result.data ?? [];
};

const useDriverSearch = (params: { search?: string; name?: string }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['drivers', params.name],
    queryFn: () => searchDgrUsers(params.name ?? ''),
  });

  const values = (data ?? []).map((u) => ({
    label: `${u.firstName} ${u.lastName}`,
    value: u.userId ?? u.id ?? '',
    phone: u.phone,
  }));

  return { values, isLoading };
};

interface DeliveryPickupInformationProps {
  allowPastDates?: boolean;
}

export const DeliveryPickupInformation = ({ allowPastDates = false }: DeliveryPickupInformationProps) => {
  const { control, setValue, watch, getValues, trigger, resetField } = useFormContext();

  const setPickupCountyAndValidate = (countyValue: string) => {
    setValue('pickupCounty', String(countyValue || ''), { shouldValidate: true, shouldTouch: true });
    // Defer trigger so it runs after RHF state updates from setValue.
    setTimeout(() => {
      void trigger('pickupCounty');
    }, 0);
  };

  // Use watch directly for additionalJobs to ensure single source of truth
  const additionalJobs = (watch('additionalJobs') || []) as { type: string; numberOfPages?: number }[];

  const pickupAddressSameAsClient = watch('pickupAddressSameAsClient') as boolean;
  const contactName = watch('contactName') as string; // Contact name stored from Step 1
  const contactPhone = watch('contactPhone') as string; // Contact phone stored from Step 1
  const assignedPickupDriverId = watch('assignedPickupDriverId') as string;
  const pickupType = watch('pickupType');
  const pickupInstructions = watch('pickupInstructions');

  // DS30: Clear 'Other' text when changing pickup type away from OTHER
  useEffect(() => {
    if (pickupType !== 'OTHER') {
      resetField('pickupTypeOther', { defaultValue: '' });
    }
  }, [pickupType, resetField]);

  const [isEditingInstructions, setIsEditingInstructions] = useState(false);

  const SearchIconsProps = {
    Icon: Search,
    iconProps: { style: { color: '#969696', height: '20px', width: '20px' } },
  };

  // Fetch counties for dropdown
  const { data: countiesData } = useQuery({
    queryKey: ['counties'],
    queryFn: async () => {
      const result = await getCounties();
      return result.data ?? [];
    },
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
  });

  const countyOptions = (countiesData ?? []).map((county) => ({
    label: county.name,
    value: String(county.countyId),
  }));

  // Fetch client address
  const clientId = watch('clientId') as string;
  const { data: clientData } = useQuery({
    queryKey: ['client-address', clientId],
    queryFn: async () => {
      if (!clientId) return null;
      const result = await deliveryService.getClientDetails(clientId);
      return result.data;
    },
    enabled: !!clientId,
  });

  // Initialize checkbox to true if not already set (ensures default behavior)
  useEffect(() => {
    const currentValue = getValues('pickupAddressSameAsClient');
    if (currentValue === undefined || currentValue === null) {
      setValue('pickupAddressSameAsClient', true);
    }
  }, []);

  // Auto-fill person name from contact
  useEffect(() => {
    if (contactName) {
      const currentName = getValues('pickupPersonName');
      if (!currentName) {
        setValue('pickupPersonName', contactName, { shouldValidate: true });
      }
    }
  }, [contactName, setValue, getValues]);

  // Auto-fill phone from contact
  useEffect(() => {
    if (contactPhone) {
      const currentPhone = getValues('pickupPhone');
      if (!currentPhone) {
        setValue('pickupPhone', contactPhone);
      }
    }
  }, [contactPhone, setValue, getValues]);

  // Auto-fill pickup address on mount/load if fields are empty
  useEffect(() => {
    if (pickupAddressSameAsClient && clientData && countiesData) {
      const currentAddress1 = getValues('pickupAddressLine1');
      if (!currentAddress1) { // Only auto-fill if empty
        const client = (clientData as any)?.data || (clientData as any) || {};
        setValue('pickupAddressLine1', client.address1 || '', { shouldValidate: true });
        setValue('pickupAddressLine2', client.address2 || '');
        setValue('pickupCity', client.city || '', { shouldValidate: true });

        const matchedCounty = countiesData?.find(c => c.name.toLowerCase() === client.county?.toLowerCase());
        setPickupCountyAndValidate(String(matchedCounty?.countyId ?? ''));

        setValue('pickupState', client.state || '', { shouldValidate: true });
        setValue('pickupZip', client.zipCode || '', { shouldValidate: true });
      }
    }
  }, [pickupAddressSameAsClient, clientData, countiesData, setValue, getValues, trigger]);

  // Fetch driver phone when driver is assigned
  const { data: driverData } = useQuery({
    queryKey: ['driver', assignedPickupDriverId],
    queryFn: async () => {
      if (!assignedPickupDriverId) return null;
      const result = await deliveryService.getUserDetails(assignedPickupDriverId);
      return result.data;
    },

    enabled: !!assignedPickupDriverId,
  });

  useEffect(() => {
    if (driverData) {
      setValue('pickupDriverPhone', driverData.phone || null);
    }
  }, [driverData, setValue]);

  const handleAddJob = () => {
    const newJob = { type: 'PRINTING' };
    setValue('additionalJobs', [...additionalJobs, newJob]);
  };

  const handleRemoveJob = (index: number) => {
    const updated = additionalJobs.filter((_, i) => i !== index);
    setValue('additionalJobs', updated);
  };

  const handleJobTypeChange = (index: number, type: string) => {
    const updated = [...additionalJobs];
    updated[index] = { type, numberOfPages: type === 'PRINTING' ? updated[index].numberOfPages : undefined };
    setValue('additionalJobs', updated);
  };

  const handleJobPagesChange = (index: number, pages: number) => {
    const updated = [...additionalJobs];
    updated[index] = { ...updated[index], numberOfPages: pages };
    setValue('additionalJobs', updated);
  };

  const handleSaveInstructions = () => {
    setIsEditingInstructions(false);
  };

  const handleCancelInstructions = () => {
    setIsEditingInstructions(false);
  };

  const handleAddInstructions = () => {
    setIsEditingInstructions(true);
  };

  const handleDeleteInstructions = () => {
    setValue('pickupInstructions', '');
    setIsEditingInstructions(false);
  };

  const handleEditInstructions = () => {
    setIsEditingInstructions(true);
  };

  return (
    <div className="flex flex-col w-full mt-11 gap-6">
      {/* Pickup Address Same as Client Checkbox */}
      <div className="grid grid-cols-1 gap-6">
        <FormFieldCheckbox
          control={control}
          name="pickupAddressSameAsClient"
          label="Pickup Address is Same as Client's Office"
          description=""
          onCheckChange={(checked) => {
            if (checked && clientData) {
              const client = (clientData as any)?.data || (clientData as any) || {};
              setValue('pickupAddressLine1', client.address1 || '', { shouldValidate: true });
              setValue('pickupAddressLine2', client.address2 || '');
              setValue('pickupCity', client.city || '', { shouldValidate: true });

              const matchedCounty = countiesData?.find(c => c.name.toLowerCase() === client.county?.toLowerCase());
              setPickupCountyAndValidate(String(matchedCounty?.countyId ?? ''));

              setValue('pickupState', client.state || '', { shouldValidate: true });
              setValue('pickupZip', client.zipCode || '', { shouldValidate: true });
            } else if (checked && !clientData) {
              // Client data not loaded yet — clear fields and trigger validation
              setValue('pickupAddressLine1', '');
              setValue('pickupAddressLine2', '');
              setValue('pickupCity', '');
              setPickupCountyAndValidate('');
              setValue('pickupState', '');
              setValue('pickupZip', '');
            } else if (!checked) {
              // Clear address fields when unchecked manually
              setValue('pickupAddressLine1', '', { shouldValidate: true });
              setValue('pickupAddressLine2', '', { shouldValidate: true });
              setValue('pickupCity', '', { shouldValidate: true });
              setPickupCountyAndValidate('');
              setValue('pickupState', '', { shouldValidate: true });
              setValue('pickupZip', '', { shouldValidate: true });
            }
          }}
        />
      </div>

      {/* Person/Entity Name and Phone */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormFieldComponent
          control={control}
          name="pickupPersonName"
          label="Person/Entity's Name"
          placeholder="Enter Name"
          required
        />
        <FormFieldPhone name="pickupPhone" label="Phone Number" placeholder="Enter Phone Number" />
      </div>

      {/* Address Fields */}
      <div className="grid grid-cols-1 gap-6">
        <FormFieldComponent
          control={control}
          name="pickupAddressLine1"
          label="Address Line 1"
          placeholder="Enter Street Address"
          required
          disabled={pickupAddressSameAsClient}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormFieldComponent
          control={control}
          name="pickupAddressLine2"
          label="Address Line 2"
          placeholder="Enter Apt, suite etc. (Optional)"
          disabled={pickupAddressSameAsClient}
        />
        <FormFieldComponent
          control={control}
          name="pickupCity"
          label="City"
          placeholder="City"
          replaceRegex={/[^a-zA-Z\s'-]/g}
          required
          disabled={pickupAddressSameAsClient}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <FormFieldSelect
          name="pickupCounty"
          label="County"
          required
          options={countyOptions}
          placeholder="Select County"
        />
        <FormFieldComponent
          control={control}
          name="pickupState"
          label="State"
          placeholder="State"
          required
          maxLength={2}
          allowNumber={false}
          allowSpecialCharacters={false}
          capitalize
          disabled={pickupAddressSameAsClient}
        />
        <FormFieldComponent
          control={control}
          name="pickupZip"
          label="Zip Code"
          placeholder="Zip Code"
          replaceRegex={/[^0-9-]/g}
          maxLength={10}
          props={{ inputMode: 'numeric' }}
          required
          disabled={pickupAddressSameAsClient}
        />
      </div>

      {/* Pickup Date */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormFieldCalendar
          control={control}
          name="pickupDate"
          label="Pickup Date"
          placeholder="Select Date"
          required
          disablePastDates={!allowPastDates}
        />
      </div>

      {/* Type of Pickup & Details */}
      <div className="flex flex-col gap-6">

        {/* Type of Pickup Header & Radios */}
        <div className="flex flex-col gap-4">
          <label className="text-base font-bold text-gray-900 flex items-center gap-1">
            Type of Pickup
            <span className="text-error align-top">*</span>
          </label>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <FormFieldRadioGroup
              name="pickupType"
              required
              options={PICKUP_TYPE_OPTIONS}
              direction="horizontal"
            />
            <Button type="button" variant="ghost" className="text-primary hover:text-primary/90 font-bold p-0 h-auto" onClick={handleAddJob}>
              <Plus className="h-4 w-4 mr-2" />
              Add Additional Job
            </Button>
          </div>
        </div>

        {/* Other Pickup Type Input */}
        {pickupType === 'OTHER' && (
          <div className="grid grid-cols-1 gap-6">
            <FormFieldComponent
              control={control}
              name="pickupTypeOther"
              label="Other"
              required
              placeholder="Enter Type of Pickup"
            />
          </div>
        )}

        {/* Additional Jobs List */}
        {additionalJobs.length > 0 && (
          <div className="flex flex-col gap-4">
            {additionalJobs.map((job, index) => (
              <div key={index} className="flex items-end gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1">
                  <div>
                    <FormFieldSelect
                      name={`additionalJobs.${index}.type`}
                      label="Additional Job"
                      sortOptions
                      options={ADDITIONAL_JOB_OPTIONS}
                      placeholder="Select Job Type"
                      onChange={(value) => handleJobTypeChange(index, value!)}
                    />
                  </div>
                  {job.type === 'PRINTING' ? (
                    <FormFieldComponent
                      control={control}
                      name={`additionalJobs.${index}.numberOfPages`}
                      label="Number of Pages"
                      placeholder="Enter Number"
                      type="number"
                      required
                      onChange={(e) => handleJobPagesChange(index, parseInt(e.target.value) || 0)}
                    />
                  ) : (
                    <div className="hidden md:block" />
                  )}
                </div>
                <button
                  type="button"
                  aria-label="Remove additional job"
                  onClick={() => handleRemoveJob(index)}
                  className="mb-4 p-0 bg-transparent border-none shadow-none hover:bg-transparent flex-shrink-0"
                >
                  <Trash2 className="h-4 w-4 text-[#D72222]" strokeWidth={1.75} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Number of Items & Time Ready */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormFieldComponent
            control={control}
            name="numberOfItems"
            label="Number of Items"
            placeholder="Enter Quantity"
            required
            type="number"
          />
          <FormFieldTimePicker
            name="timeReady"
            label="Time Ready"
            required
            timeFields={['H', 'M']}
            use12HourFormat
            returnFormattedValue
          />
        </div>

      </div>

      {/* Driver Assignment */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormFieldSearch
          name="assignedPickupDriverId"
          label="Assign Pickup Driver"
          placeholder="Search Driver"
          props={SearchIconsProps}
          searchFieldName="name"
          api={useDriverSearch}
        />
        <FormFieldComponent
          control={control}
          name="pickupDriverPhone"
          label="Driver's Phone Number"
          placeholder="Driver Phone"
          disabled
          disabledStyle="bg-[#F5F5F5]"
        />
      </div>

      {/* Instructions */}
      <div className="flex flex-col gap-2">

        {/* Add Button - Only visible when no instructions and not editing */}
        {!isEditingInstructions && !pickupInstructions && (
          <div className="flex justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={handleAddInstructions} className="text-primary font-bold hover:text-primary/90 p-0 h-auto">
              <Plus className="h-4 w-4 mr-2" />
              Add Instructions
            </Button>
          </div>
        )}

        {/* Edit Mode */}
        {isEditingInstructions && (
          <div className="flex flex-col gap-2">
            <FormFieldTextArea
              name="pickupInstructions"
              label="Instructions"
              placeholder="Enter Instructions"
              rows={3}
            />
            <div className="flex justify-end gap-2 mt-2">
              <Button type="button" variant="outline" size="sm" onClick={handleCancelInstructions} className="border-[#CAC8C8] text-[#0A2240] hover:bg-slate-50">Cancel</Button>
              <Button type="button" size="sm" onClick={handleSaveInstructions} className="bg-[#0A2240] text-white hover:bg-[#0A2240]/90">Save</Button>
            </div>
          </div>
        )}

        {/* View Mode */}
        {!isEditingInstructions && pickupInstructions && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-bold text-gray-900">Instructions</label>
            <div className="flex items-center gap-2">
              <div className="flex-grow">
                <div className="min-h-[40px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 flex items-center">
                  {pickupInstructions}
                </div>
              </div>
              <button
                type="button"
                aria-label="Edit instructions"
                onClick={handleEditInstructions}
                className="p-0 bg-transparent hover:bg-transparent border-none shadow-none"
              >
                <Edit2 className="h-5 w-5 text-primary" />
              </button>
              <Button type="button" variant="ghost" size="icon" onClick={handleDeleteInstructions} className="h-8 w-8 p-0 hover:bg-transparent">
                <Trash2 className="h-5 w-5 text-[#D72222]" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
